import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import {
  InventaireSession,
  MobileImportPayload,
  MobileImportResponse,
  QrCodePayload,
  ScannedArticle,
} from '../../shared/models';
import { StorageService } from './storage.service';
import { ArticleCatalogService, ResolvedCatalogEntry } from './article-catalog.service';
import { environment } from '../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class InventaireService {
  private sessionSubject = new BehaviorSubject<InventaireSession | null>(null);
  session$ = this.sessionSubject.asObservable();

  constructor(
    private http: HttpClient,
    private storageService: StorageService,
    private articleCatalogService: ArticleCatalogService,
  ) {
    this.articleCatalogService.catalog$.subscribe((catalog) => {
      if (catalog?.codeSite) this.setCodeSite(catalog.codeSite);
    });
  }

  get currentSession(): InventaireSession | null {
    return this.sessionSubject.value;
  }

  async loadFromStorage(): Promise<InventaireSession | null> {
    const session = await this.storageService.getSession();
    this.sessionSubject.next(session);
    return session;
  }

  startSession(payload: QrCodePayload): void {
    const session: InventaireSession = {
      idInventaire: payload.id,
      codeBu: payload.codeBu,
      codeSite: payload.codeSite,
      articles: [],
      isSent: false,
      createdAt: new Date(),
    };
    this.sessionSubject.next(session);
    this.persist();
  }

  /**
   * Instant barcode add with synchronous local resolution against the prefetched catalog.
   * If the barcode is unknown, the article is marked 'error' — the user can later hit
   * "Actualiser catalogue" to refetch and re-resolve.
   */
  addBarcode(barcode: string): { article: ScannedArticle; isNew: boolean } {
    const session = this.currentSession;
    if (!session) throw new Error('No active session');

    const existing = session.articles.find((a) => a.barcode === barcode);
    if (existing) {
      existing.quantiteInventorie += 1;
      existing.scannedAt = new Date();
      this.sessionSubject.next({ ...session });
      this.persist();
      return { article: existing, isNew: false };
    }

    const scanned: ScannedArticle = {
      barcode,
      quantiteInventorie: 1,
      isSubReference: false,
      scannedAt: new Date(),
      resolveStatus: 'pending',
      resolveRetries: 0,
    };
    this.applyCatalogEntry(scanned, this.articleCatalogService.resolve(barcode));
    session.articles.push(scanned);
    this.sessionSubject.next({ ...session });
    this.persist();

    return { article: scanned, isNew: true };
  }

  /**
   * After a catalog refresh, re-resolve all articles currently in 'error' status.
   * Returns the number of articles that flipped to 'resolved'.
   */
  reresolveErrored(): number {
    const session = this.currentSession;
    if (!session) return 0;
    let resolvedCount = 0;
    for (const art of session.articles) {
      if (art.resolveStatus !== 'error' && art.resolveStatus !== 'pending') continue;
      const entry = this.articleCatalogService.resolve(art.barcode);
      if (entry) {
        this.applyCatalogEntry(art, entry);
        resolvedCount += 1;
      }
    }
    if (resolvedCount > 0) {
      this.sessionSubject.next({ ...session });
      this.persist();
    }
    return resolvedCount;
  }

  /**
   * Retry a single article's resolution (e.g. from the list "retry" button).
   * Same semantics as reresolveErrored but scoped to one barcode.
   */
  retryResolve(barcode: string): boolean {
    const session = this.currentSession;
    if (!session) return false;
    const article = session.articles.find((a) => a.barcode === barcode);
    if (!article || article.resolveStatus === 'resolved') return false;
    const entry = this.articleCatalogService.resolve(barcode);
    if (!entry) return false;
    this.applyCatalogEntry(article, entry);
    this.sessionSubject.next({ ...session });
    this.persist();
    return true;
  }

  private applyCatalogEntry(target: ScannedArticle, entry: ResolvedCatalogEntry | null): void {
    if (!entry) {
      target.resolveStatus = 'error';
      return;
    }
    const src = entry.article;
    target.idArticle = src.idArticle;
    target.codeArticle = src.codeArticle;
    target.libelleArticle = src.libelle;
    target.libelleFamille = src.libelleFamille;
    target.libelleSousFamille = src.libelleSousFamille;
    target.isSubReference = entry.isSubReference;
    target.prixAchat = entry.sousRef?.prixAchatTarifHt ?? src.prixAchatTarifHt;
    if (entry.sousRef) {
      target.idArticleSousReference = entry.sousRef.idArticleSousReference;
      target.libelleSousReference = entry.sousRef.libelleSousRef;
      target.codeSousRef = entry.sousRef.codeSousRef;
    } else {
      target.idArticleSousReference = undefined;
      target.libelleSousReference = undefined;
      target.codeSousRef = undefined;
    }
    const eligibilitySrc = entry.sousRef ?? src;
    target.isAccessible = eligibilitySrc.isAccessible;
    target.isInventoryManaged = eligibilitySrc.isInventoryManaged;
    target.isActive = eligibilitySrc.isActive;
    target.resolveStatus = 'resolved';
  }

  isIneligible(article: ScannedArticle): boolean {
    if (article.resolveStatus !== 'resolved') return false;
    return (
      article.isAccessible === false ||
      article.isInventoryManaged === false ||
      article.isActive === false
    );
  }

  getIneligibilityReasons(article: ScannedArticle): string[] {
    const reasons: string[] = [];
    if (article.resolveStatus !== 'resolved') return reasons;
    if (article.isAccessible === false) reasons.push('Non accessible sur ce site');
    if (article.isInventoryManaged === false) reasons.push('Non géré en stock');
    if (article.isActive === false) reasons.push('Article inactif');
    return reasons;
  }

  get ineligibleCount(): number {
    const session = this.currentSession;
    if (!session) return 0;
    return session.articles.filter((a) => this.isIneligible(a)).length;
  }

  addBarcodeOffline(barcode: string): ScannedArticle {
    return this.addBarcode(barcode).article;
  }

  updateQuantity(barcode: string, quantity: number): void {
    const session = this.currentSession;
    if (!session) return;
    const article = session.articles.find((a) => a.barcode === barcode);
    if (article) {
      article.quantiteInventorie = Math.max(0, quantity);
      if (article.quantiteInventorie === 0) {
        session.articles = session.articles.filter((a) => a.barcode !== barcode);
      }
      this.sessionSubject.next({ ...session });
      this.persist();
    }
  }

  removeArticle(barcode: string): void {
    const session = this.currentSession;
    if (!session) return;
    session.articles = session.articles.filter((a) => a.barcode !== barcode);
    this.sessionSubject.next({ ...session });
    this.persist();
  }

  setCodeSite(codeSite: string): void {
    const session = this.currentSession;
    if (!session) return;
    if (session.codeSite === codeSite) return;
    session.codeSite = codeSite;
    this.sessionSubject.next({ ...session });
    this.persist();
  }

  submitInventory(): Observable<MobileImportResponse> {
    const session = this.currentSession;
    if (!session) throw new Error('No active session');
    if (!session.codeSite) {
      throw new Error('codeSite manquant : charge le catalogue articles avant l\'envoi');
    }

    if (environment.mockMode) {
      return of({
        idInventaireImport: 0,
        nombreLignesCrees: session.articles.length,
        origine: 'MOBILE',
        dateImport: new Date().toISOString(),
        erreurs: [],
      });
    }

    const payload: MobileImportPayload = {
      idInventaire: session.idInventaire,
      codeSite: session.codeSite,
      codeBu: session.codeBu,
      lignes: session.articles.map((a) => ({
        barcode: a.barcode,
        quantiteInventorie: a.quantiteInventorie,
      })),
    };

    return this.http.post<MobileImportResponse>(
      `${environment.apiBaseUrl}/common/ligne-inventaire/${session.idInventaire}/import/mobile`,
      payload,
      {
        params: { codeBu: session.codeBu },
        headers: { 'X-Silent': 'true' },
      }
    );
  }

  markAsSent(): void {
    const session = this.currentSession;
    if (!session) return;
    session.isSent = true;
    session.sentAt = new Date();
    this.sessionSubject.next({ ...session });
    this.persist();
  }

  get totalArticles(): number {
    return this.currentSession?.articles.length ?? 0;
  }

  get totalQuantity(): number {
    return (
      this.currentSession?.articles.reduce(
        (sum, a) => sum + a.quantiteInventorie,
        0
      ) ?? 0
    );
  }

  getGroupedArticles(): Map<string, ScannedArticle[]> {
    const grouped = new Map<string, ScannedArticle[]>();
    if (!this.currentSession) return grouped;

    for (const article of this.currentSession.articles) {
      const key = article.libelleFamille || 'Non classifié';
      if (!grouped.has(key)) {
        grouped.set(key, []);
      }
      grouped.get(key)!.push(article);
    }
    return grouped;
  }

  async clearAndReset(): Promise<void> {
    const idInventaire = this.currentSession?.idInventaire;
    await this.storageService.clearSession();
    if (idInventaire != null) {
      await this.articleCatalogService.clearStored(idInventaire);
    } else {
      this.articleCatalogService.clear();
    }
    this.sessionSubject.next(null);
  }

  private persist(): void {
    const session = this.currentSession;
    if (session) {
      this.storageService.saveSession(session);
    }
  }
}
